import React, { useEffect, useRef, useState } from 'react';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.css';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import useToken from '../App/useToken';
import '../UserResponsive.css';

function AdminSpecialPredictions() {
    const { token } = useToken();
    const toast = useRef(null);
    const [results, setResults] = useState([]);
    const [officialSemifinalists, setOfficialSemifinalists] = useState(['', '', '', '']);
    const [predictions, setPredictions] = useState([]);
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [playersJson, setPlayersJson] = useState('[]');
    const [globalFilter, setGlobalFilter] = useState(null);

    useEffect(() => {
        loadData();
        loadPlayersJson();
    }, []);

    const authHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + token,
    };

    const loadData = () => {
        fetch(process.env.REACT_APP_URL + 'admin/special-predictions', {
            method: 'GET',
            headers: authHeaders,
        })
            .then((response) => response.json())
            .then((data) => {
                setResults(data.resultados || []);
                setPredictions(data.predicciones || []);
                setTeams(data.teams || []);
                setPlayers(data.players || []);
                const selected = data.semifinalistas_oficiales || [];
                setOfficialSemifinalists([selected[0] || '', selected[1] || '', selected[2] || '', selected[3] || '']);
            });
    };

    const loadPlayersJson = () => {
        fetch(process.env.REACT_APP_URL + 'admin/special-players-json', {
            method: 'GET',
            headers: authHeaders,
        })
            .then((response) => response.json())
            .then((data) => setPlayersJson(data.jugadores_json || '[]'));
    };

    const savePlayersJson = () => {
        fetch(process.env.REACT_APP_URL + 'admin/special-players-json', {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({ jugadores_json: playersJson }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.code === 200) {
                    toast.current.show({ severity: 'success', summary: 'Jugadores', detail: data.mensaje, life: 3000 });
                    loadData();
                    loadPlayersJson();
                }
                else {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: data.mensaje || 'No se pudo guardar el JSON', life: 5000 });
                }
            });
    };

    const onResultChange = (categoryId, field, value) => {
        setResults(results.map((result) => {
            if (result.category_id !== categoryId) {
                return result;
            }

            return {
                ...result,
                result_team_id: field === 'result_team_id' ? value : null,
                result_player_id: field === 'result_player_id' ? value : null,
            };
        }));
    };

    const onSemifinalistChange = (index, value) => {
        const next = [...officialSemifinalists];
        next[index] = value;
        setOfficialSemifinalists(next);
    };

    const getTeamOptionsForSemifinalist = (index) => {
        const selected = officialSemifinalists.filter((teamId, selectedIndex) => teamId && selectedIndex !== index).map((teamId) => Number(teamId));
        return teams.filter((team) => !selected.includes(Number(team.id)));
    };

    const teamOptionTemplate = (option) => {
        if (!option) {
            return null;
        }

        return (
            <div className="team-cell">
                <img src={process.env.REACT_APP_URL_IMAGES + option.bandera} onError={(e) => e.target.src='https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png'} alt={option.nombre} className="product-image" />
                <span>{option.nombre}</span>
            </div>
        );
    };

    const playerOptionTemplate = (option) => {
        if (!option) {
            return null;
        }

        return (
            <div className="team-cell">
                <img src={process.env.REACT_APP_URL_IMAGES + option.bandera} onError={(e) => e.target.src='https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png'} alt={option.team_name} className="product-image" />
                <span>{option.name} - {option.team_name}</span>
            </div>
        );
    };

    const getPlayerOptions = (result) => {
        if (result.tipo === 'goalkeeper') {
            return players.filter((player) => player.position === 'portero');
        }

        return players.filter((player) => player.position === 'jugador');
    };

    const renderOfficialResultInput = (result) => {
        if (result.tipo === 'team') {
            return (
                <Dropdown
                    value={result.result_team_id || null}
                    options={teams}
                    optionLabel="nombre"
                    optionValue="id"
                    itemTemplate={teamOptionTemplate}
                    valueTemplate={teamOptionTemplate}
                    placeholder="Selecciona una seleccion"
                    filter
                    onChange={(e) => onResultChange(result.category_id, 'result_team_id', e.value)}
                />
            );
        }

        return (
            <Dropdown
                value={result.result_player_id || null}
                options={getPlayerOptions(result)}
                optionLabel="name"
                optionValue="id"
                itemTemplate={playerOptionTemplate}
                valueTemplate={playerOptionTemplate}
                placeholder={result.tipo === 'goalkeeper' ? 'Selecciona un portero' : 'Selecciona un jugador'}
                filter
                onChange={(e) => onResultChange(result.category_id, 'result_player_id', e.value)}
            />
        );
    };

    const saveResults = () => {
        const selectedSemifinalists = officialSemifinalists.filter(Boolean);
        const uniqueSemifinalists = [...new Set(selectedSemifinalists.map((teamId) => Number(teamId)))];

        if (selectedSemifinalists.length > 0 && (selectedSemifinalists.length !== 4 || uniqueSemifinalists.length !== 4)) {
            toast.current.show({ severity: 'error', summary: 'Semifinalistas', detail: 'Debes seleccionar 4 semifinalistas oficiales diferentes', life: 4000 });
            return;
        }

        fetch(process.env.REACT_APP_URL + 'admin/special-prediction-results', {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({ resultados: results, semifinalistas: uniqueSemifinalists }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.code === 200) {
                    toast.current.show({ severity: 'success', summary: 'Resultados', detail: data.mensaje, life: 3000 });
                    loadData();
                }
                else {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: data.mensaje || 'No se pudo guardar', life: 4000 });
                }
            });
    };

    const header = (
        <div className="table-header">
            <h5 className="mx-0 my-1">Revision de apuestas especiales</h5>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Buscar..." />
            </span>
        </div>
    );

    return (
        <div className="datatable-crud-demo user-mobile-page">
            <Toast ref={toast} />

            <div className="card mb-4">
                <h5>Jugadores disponibles por JSON</h5>
                <p className="text-700 text-sm line-height-3">
                    Usa formato JSON valido. Quitar un jugador del JSON lo desactiva para nuevas apuestas, sin romper apuestas ya guardadas.
                </p>
                <InputTextarea value={playersJson} rows={12} autoResize className="w-full" onChange={(e) => setPlayersJson(e.target.value)} />
                <Button label="Guardar jugadores" icon="pi pi-save" className="p-button-success mt-3" onClick={savePlayersJson} />
            </div>

            <div className="card mb-4">
                <h5>Resultados oficiales especiales</h5>
                <div className="p-fluid grid">
                    {[0, 1, 2, 3].map((index) => (
                        <div className="field col-12 md:col-6" key={`official-semifinalist-${index}`}>
                            <label htmlFor={`official-semifinalist-${index}`}>Semifinalista oficial {index + 1}</label>
                            <Dropdown
                                id={`official-semifinalist-${index}`}
                                value={officialSemifinalists[index] || null}
                                options={getTeamOptionsForSemifinalist(index)}
                                optionLabel="nombre"
                                optionValue="id"
                                itemTemplate={teamOptionTemplate}
                                valueTemplate={teamOptionTemplate}
                                placeholder="Selecciona una seleccion"
                                filter
                                onChange={(e) => onSemifinalistChange(index, e.value)}
                            />
                        </div>
                    ))}

                    {results.map((result) => (
                        <div className="field col-12 md:col-6" key={result.category_id}>
                            <label htmlFor={`result-${result.category_id}`}>{result.categoria}</label>
                            {renderOfficialResultInput(result)}
                        </div>
                    ))}
                </div>
                <Button label="Guardar resultados y recalcular puntos" icon="pi pi-refresh" className="p-button-success" onClick={saveResults} />
            </div>

            <div className="card">
                <DataTable value={predictions} paginator rows={20} rowsPerPageOptions={[10, 20, 50]}
                    className="user-mobile-table" globalFilter={globalFilter} header={header} responsiveLayout="scroll" scrollDirection="both">
                    <Column field="usuario" header="Usuario" sortable style={{ minWidth: '10rem' }}></Column>
                    <Column field="categoria" header="Categoria" sortable style={{ minWidth: '12rem' }}></Column>
                    <Column field="respuesta_texto" header="Respuesta usuario" sortable style={{ minWidth: '16rem' }}></Column>
                    <Column field="resultado_texto" header="Resultado oficial" sortable style={{ minWidth: '16rem' }}></Column>
                    <Column field="puntos" header="Puntos" sortable style={{ minWidth: '6rem' }}></Column>
                </DataTable>
            </div>
        </div>
    );
}

export default AdminSpecialPredictions;
